// BrowserList.tsx
import React, {
  useState,
  useRef,
  useCallback,
  ReactNode,
  MouseEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';

import TableHeader from '@components/TableHeader';
import TableRow from '@components/TableRow';
import TableCell from '@components/TableCell';
import SimpleContextMenu, {
  SimpleContextMenuItem,
} from '@components/ContextMenu/SimpleContextMenu';
import styles from './BrowserList.module.scss';

import { getContextMenuItemsForRow } from 'config/browserConfig';
import { Study } from '@models/Study';
import { useBrowserPage } from '../useBrowserPage';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { useBufferPool } from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { useAuthPage } from '@features/auth/useAuthPage';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import { MouseTooltip } from '@components/Tooltip';
import { getSeriesByStudy } from '@api/series';
import { SeriesRes } from '@models/SeriesBackend';
import { toCamelCase } from 'utils/caseConverter';
import useFileOperationModal from '@features/analysis/analysisModal/FileOperationModal/FileOperationModal';
import { useServerFeatureState } from 'config/serverFeatureState';

type BrowserColumn = {
  id: string;
  label: string;
  sortable: boolean;
};

interface BrowserListProps {
  items: Study[];
  loading: boolean;
  refetch: () => void;

  // ✅ BrowserPage에서 계산된 "보이는 컬럼(순서 포함)"
  columns: BrowserColumn[];

  // ✅ 드래그로 컬럼 순서 변경 요청
  onMoveColumn: (fromId: string, toId: string) => void;

  handleClickRefresh: () => void;
}

const BrowserList: React.FC<BrowserListProps> = ({
  items,
  loading,
  refetch,
  columns,
  onMoveColumn,
  handleClickRefresh,
}) => {
  const navigate = useNavigate();
  const { user, accessToken } = useAuthPage();

  // ===== 기존 row context menu =====
  const [contextMenuItems, setContextMenuItems] = useState<SimpleContextMenuItem[]>([]);
  const onCloseMenu = () => setContextMenuItems([]);

  const { handleOpenFileOperationModal } = useFileOperationModal();
  const { selectedLayoutConfig } = useAnalysisPage();
  const { updateSelectedPanelConfig } = usePanelContainer();
  const { selectedStudyId, updateSelectedStudy } = useBrowserPage();
  const { clearPool } = useBufferPool();
  //const deleteStudyMutation = useDeleteStudyById();

  const isAdmin = user?.role.name === 'admin';
  const serverFeatureState = useServerFeatureState(accessToken);

  // ===== tooltip (기존) =====
  const [showTooltip, setShowTooltip] = useState(false);
  const hoverTimeout = 410;
  const [tooltipContent, setTooltipContent] = useState<ReactNode>();
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const lastPosRef = useRef({ x: 0, y: 0 });
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // ===== reorder (드래그) =====
  const [draggingColId, setDraggingColId] = useState<string | null>(null);
  const [dragOverColId, setDragOverColId] = useState<string | null>(null);

  const handleHeaderDragStart = useCallback((e: React.DragEvent, id: string) => {
    setDraggingColId(id);
    setDragOverColId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  }, []);

  const handleHeaderDragOver = useCallback(
    (e: React.DragEvent, overId: string) => {
      if (!draggingColId) return;
      e.preventDefault(); // ✅ 이거 없으면 drop이 막혀서 "안 먹는 느낌"이 남
      e.dataTransfer.dropEffect = 'move';
      if (dragOverColId !== overId) setDragOverColId(overId);
    },
    [draggingColId, dragOverColId],
  );

  const handleHeaderDrop = useCallback(
    (e: React.DragEvent, dropId: string) => {
      e.preventDefault();
      const fromId = draggingColId ?? e.dataTransfer.getData('text/plain');
      if (!fromId) return;

      onMoveColumn(fromId, dropId);

      setDraggingColId(null);
      setDragOverColId(null);
    },
    [draggingColId, onMoveColumn],
  );

  const handleHeaderDragEnd = useCallback(() => {
    setDraggingColId(null);
    setDragOverColId(null);
  }, []);

  // ===== 기존 로직들 (컨텍스트 메뉴/툴팁/클릭) =====
  const handleContextMenu = useCallback(
    (event: React.MouseEvent, rowId: string) => {
      event.preventDefault();
      event.stopPropagation();

      const menuItems = getContextMenuItemsForRow(
        rowId,
        isAdmin,
        onCloseMenu,
        (id) => handleOpenFileOperationModal(id,handleClickRefresh,'delete'),
        (id) => handleOpenFileOperationModal(id,handleClickRefresh,'download'),
        (id) => handleOpenFileOperationModal(id, handleClickRefresh, 'recalc'),
        (id) => handleOpenFileOperationModal(id, handleClickRefresh, 're_ctffr'),
        (id) => handleOpenFileOperationModal(id, handleClickRefresh, 're_generate_data'),
        (id) => console.log(`????(${id})->send`),
        serverFeatureState,
      );

      setContextMenuItems(menuItems);
    },
    [isAdmin, handleOpenFileOperationModal, handleClickRefresh, serverFeatureState],
  );

  const handleStudyClick = useCallback(
    (event: React.MouseEvent, study: Study) => {
      if (selectedStudyId !== study.id) {
        clearPool();
        updateSelectedStudy(study);
      }
      updateSelectedPanelConfig(
        selectedLayoutConfig.panelConfig.filter((p) => p.panelId === 'DICOMPanel')[0],
      );
      navigate(`/analysis/${study.id}`);
    },
    [
      selectedStudyId,
      clearPool,
      updateSelectedStudy,
      updateSelectedPanelConfig,
      selectedLayoutConfig.panelConfig,
      navigate,
    ],
  );

  function parseStudySummary(studyId: string) {
    setTooltipContent(
      <div className="info-tooltip-skeleton">
        <div className="row" />
        <div className="row" />
        <div className="row" />
      </div>,
    );

    getSeriesByStudy({ accessToken, params: { id: studyId } }).then((res) => {
      if (res.status !== 200) return;
      const seriesData = (res as unknown as SeriesRes).data;
      setTooltipContent(
        <div className={styles['info-tooltip']}>
          <table className={styles['info-tooltip-table']}>
            <tbody>
              {seriesData.map((r, idx) => {
                const imageTag = toCamelCase<typeof r.imageTag>(r.imageTag);
                return (
                  <tr key={idx}>
                    <th>
                      {`${r.series_description.length === 0 ? 'No Description' : r.series_description}(${imageTag.depthInterpolated})`}
                    </th>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>,
      );
    });
  }

  const clearHoverTimer = () => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = undefined;
    }
  };

  const scheduleHover = (key: string) => {
    clearHoverTimer();
    hoverTimerRef.current = setTimeout(() => {
      const { x, y } = lastPosRef.current;
      setTooltipPos({ x, y });
      parseStudySummary(key);
      setShowTooltip(true);
    }, hoverTimeout);
  };

  const handleMouseEnter = (e: MouseEvent, key: string) => {
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    setShowTooltip(false);
    scheduleHover(key);
  };
  const handleMouseMove = (e: MouseEvent, key: string) => {
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    if (!showTooltip) scheduleHover(key);
  };
  const handleMouseLeave = () => {
    clearHoverTimer();
  };

  return (
    <>
    {/* <div className={styles.content}>
      <table className={styles.browserListTable}></table> */}
      <div className={styles.content}>
        <div className={styles.topBar}>
          <div className={styles.tableHeader}>
            <div className={styles.tableHeaderContent}>
              {columns.map(({ id, label, sortable }) => (
                <TableHeader
                  key={id}
                  id={id}
                  label={label}
                  allowsSorting={sortable}
                  draggable
                  isDragging={draggingColId === id}
                  isDragOver={dragOverColId === id && draggingColId !== id}
                  onDragStart={handleHeaderDragStart}
                  onDragOver={handleHeaderDragOver}
                  onDrop={handleHeaderDrop}
                  onDragEnd={handleHeaderDragEnd}
                />
              ))}
            </div>
          </div>
        </div>
        <div className={styles.tableBody}>
          <table className={styles.browserListTable}>
            <tbody>
              {items.map((row, index) => (
                <TableRow
                  key={row.id}
                  id={row.id}
                  onClick={(e) => handleStudyClick(e, row)}
                  onContextMenu={
                    isAdmin ? (e) => handleContextMenu(e, row.id) : undefined
                  }
                  onMouseEnter={(e) => handleMouseEnter(e, row.id)}
                  onMouseMove={(e) => handleMouseMove(e, row.id)}
                  onMouseLeave={() => handleMouseLeave()}
                  className={index % 2 === 0 ? '' : styles.evenRow}
                >
                  {columns.map((col) => {
                    if (col.id === 'status') {
                      return (
                        <TableCell key={col.id} isDesc={false}>
                          {/* <TableCell key={col.id} isDesc={col.id==='studyDescription'}> */}
                          <div
                            className={`${styles['status-list']} ${styles[`status-${row.status}`]}`}
                          >
                            <span className={styles['status-text']}>
                              {row.status ?? '-'}
                            </span>
                          </div>
                        </TableCell>
                      );
                    }
                    const text = (() => {
                      switch (col.id) {
                        case 'patientId':
                          return row.patient?.patientId ?? '-';
                        case 'patientName':
                          return row.patient?.patientName ?? '-';
                        case 'patientBirthdate':
                          return row.patient?.patientBirthdate ?? '-';
                        default: {
                          const value = row[col.id as keyof Study];
                          if (Array.isArray(value)) return value.join(', ');
                          if (typeof value === 'object') return JSON.stringify(value);
                          return String(value ?? '-');
                        }
                      }
                    })();
                    return (
                      <TableCell key={col.id} isDesc={col.id === 'studyDescription'}>
                        <span className={styles['cell-text']}>{text}</span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 기존 row context menu */}
      {contextMenuItems.length !== 0 && (
        <SimpleContextMenu items={contextMenuItems} onClose={onCloseMenu} />
      )}

      {showTooltip && (
        <MouseTooltip x={tooltipPos.x} y={tooltipPos.y} offsetX={2} offsetY={2}>
          {tooltipContent}
        </MouseTooltip>
      )}
    </>
  );
};

export default BrowserList;
